import { useState, useEffect } from 'react';
import { ChevronRight, Wrench, CheckCircle2 } from 'lucide-react';

const TRAVAUX_CONFORT = [
  { id: 'sdb', label: 'Rénovation salle de bain' },
  { id: 'volets', label: 'Volets roulants / stores' },
  { id: 'carrelage', label: 'Carrelage / revêtements sol' },
  { id: 'cuisine', label: 'Cuisine' },
  { id: 'peinture', label: 'Peinture / revêtements muraux' },
  { id: 'electricite', label: 'Électricité' },
  { id: 'plomberie', label: 'Plomberie' },
  { id: 'menuiserie', label: 'Menuiserie (portes, fenêtres classiques)' },
  { id: 'domotique', label: 'Domotique / automatismes' },
  { id: 'amenagement_ext', label: 'Aménagement extérieur' }
];

const TravauxConfortWidget = ({ onDataChange, initialData = null, collapsed = true, readOnly = false }) => {
  const [ouvert, setOuvert] = useState(!collapsed);
  const [selectionnes, setSelectionnes] = useState(initialData?.selectionnes || []);
  const [autreTexte, setAutreTexte] = useState(initialData?.autreTexte || '');
  const [notes, setNotes] = useState(initialData?.notes || '');

  const toggleTravail = (id) => {
    if (readOnly) return;
    setSelectionnes(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (onDataChange) {
      onDataChange({ selectionnes, autreTexte, notes });
    }
  }, [selectionnes, autreTexte, notes]);

  return (
    <div data-testid="travaux-confort-widget" className="bg-gradient-to-r from-slate-800/60 to-slate-700/40 border border-slate-600/50 rounded-xl p-4">
      <button
        onClick={() => setOuvert(!ouvert)}
        className="w-full flex items-center justify-between text-left"
        data-testid="travaux-confort-toggle"
      >
        <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2 flex-wrap">
          <Wrench className="w-4 h-4" />
          Travaux non subventionnables (confort)
          {selectionnes.length > 0 && (
            <span className="bg-slate-500/30 text-slate-300 px-2 py-0.5 rounded text-xs normal-case">
              {selectionnes.length} sélectionné(s)
            </span>
          )}
        </h3>
        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${ouvert ? 'rotate-90' : ''}`} />
      </button>

      {ouvert && (
        <div className="mt-4 space-y-4">
          <p className="text-xs text-slate-400">
            Le client souhaite aussi réaliser d'autres travaux de confort dans son logement ?
          </p>

          <div className="grid grid-cols-2 gap-2">
            {TRAVAUX_CONFORT.map(travail => {
              const selected = selectionnes.includes(travail.id);
              return (
                <button
                  key={travail.id}
                  onClick={() => toggleTravail(travail.id)}
                  disabled={readOnly}
                  data-testid={`travaux-confort-${travail.id}`}
                  className={`p-3 rounded-lg border transition-colors text-left flex items-center gap-2 ${
                    selected
                      ? 'bg-slate-600/30 border-slate-400/50'
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-500/50'
                  }`}
                >
                  {selected ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border border-slate-600 shrink-0" />
                  )}
                  <span className={`text-sm ${selected ? 'text-white' : 'text-slate-400'}`}>
                    {travail.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Autre travaux - champ libre */}
          <div>
            <label className="block text-xs text-slate-400 mb-1">Autre(s) travaux</label>
            <input
              type="text"
              value={autreTexte}
              onChange={e => setAutreTexte(e.target.value)}
              disabled={readOnly}
              data-testid="travaux-confort-autre"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-slate-400 focus:outline-none"
              placeholder="Précisez d'autres travaux souhaités..."
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs text-slate-400 mb-1">Notes complémentaires</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              disabled={readOnly}
              data-testid="travaux-confort-notes"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-slate-400 focus:outline-none"
              rows={2}
              placeholder="Détails sur les travaux souhaités..."
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TravauxConfortWidget;
